import { errorMessage } from "utils/api/errors";

// Legacy `errors: [...]` join. Only plain-string arrays keep the old `join`; arrays holding
// objects used to render "[object Object]", so those go through the shared helper instead.
const joinErrors = errors => {
  if (errors.every(item => typeof item === "string")) {
    return errors.join(", ");
  }
  return errorMessage({ json: { errors } }, errors.join(", "));
};

export const formatErrorMessage = e => {
  if (!e) {
    return "Unknown Error";
  }
  // Case the error has response
  if (e.response && e.response.data) {
    const {
      data: { errors }
    } = e.response;
    if (typeof errors === "string" || errors instanceof String) {
      return errors;
    }
  }
  // Case the error has single error response
  if (e.response && e.response.data) {
    const {
      data: { error, errors }
    } = e.response;
    if (typeof error === "string" || error instanceof String) {
      return error;
    }
    if (typeof errors === "string" || errors instanceof String) {
      return errors;
    }
    if (Array.isArray(errors)) {
      return joinErrors(errors);
    }
  }
  // Case the error is a standard Javascript Error (https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Global_Objects/Error)
  if (e.message) {
    return e.message;
  }
  // Case the error comes from API/Backend
  if (e.json && e.json.errors) {
    const { errors } = e.json;
    if (typeof errors === "string" || errors instanceof String) {
      return errors;
    }
    if (Array.isArray(errors)) {
      return joinErrors(errors);
    }
  }
  // Case the error comes from api.js
  if (e.errors) {
    const { errors } = e;
    if (typeof errors === "string" || errors instanceof String) {
      return errors;
    }
    if (Array.isArray(errors)) {
      return joinErrors(errors);
    }
  }
  // Unknown object shape: let the shared helper read the v1 envelope / DRF `detail` / field maps
  // before dumping JSON. It never throws, and strings keep the old behaviour below.
  if (typeof e === "object") {
    const text = errorMessage(e, "");
    if (text) {
      return text;
    }
  }
  // Case the error is an object, but we don't know the format — use safe stringify to avoid
  // circular structure crash (e.g. if a DOM element is accidentally passed as an error).
  try {
    return JSON.stringify(e);
  } catch (_) {
    return String(e);
  }
};

// rails-api answers 409 with code "store_disconnected" when the store's platform rejected our
// access (the merchant removed our Wix app). Only reconnecting the store helps, so it gets its
// own message instead of "contact our support".
export const STORE_DISCONNECTED_ERROR_CODE = "store_disconnected";
export const STORE_DISCONNECTED_TEXT =
  "Your Wix store is disconnected from Spocket. Please reconnect it and try again.";

// Text for a failed payment provider checkout: the disconnected-store message when that is the
// reason, the given fallback otherwise.
export const paymentProviderErrorText = (e, fallback) =>
  e?.response?.data?.code === STORE_DISCONNECTED_ERROR_CODE ? STORE_DISCONNECTED_TEXT : fallback;

const searchForString = (string, substring) => string.includes(substring);

// Change error message text based on https://docs.google.com/document/d/1KJPwOZgS5yzgEUbvxhfuRnKlXjkDxtK5NdT-sqR2idw/edit#heading=h.zchywovxr1yo
// Receive error string ex: "Problem while adding the payment method. Error message : This transaction has been declined."
// Format it to see if the case text exists in error string - if it does, display modal error message
export const formatPaymentErrorMessage = ({ text, type }) => {
  const declinedText = [
    "Your transaction is declined. Please contact your card issuer bank to resolve this issue or retry using a different credit card.",
    "error"
  ];
  const cvvText = ["Card code verification (CVV) is required. Please add your CVV and try again.", "warning"];
  switch (true) {
    case searchForString(text, "invalid zip/postal code"):
      return ["There is an error with your zip/postal code.", "warning"];
    case searchForString(text, "This transaction has been declined"):
      return declinedText;
    case searchForString(
      text,
      "The transaction has been declined because of an AVS mismatch. The address provided does not match the billing address of the cardholder."
    ):
      return ["The address provided does not match the billing address of the cardholder.", "warning"];
    case searchForString(text, "The credit card has expired"):
      return ["Your credit card has expired. Please use a new card to process your transaction.", "error"];
    case searchForString(text, "Customer Profile ID or Customer Payment Profile ID not found."):
      return [
        "We are missing your credit card information. Please use a different card to process your transaction.",
        "error"
      ];
    case searchForString(text, "An error occurred during processing. Please try again."):
      return [
        "Network error occurred during processing. Please try again or contact your bank to process this transaction.",
        "error"
      ];
    case searchForString(
      text,
      "Street address and postal code do not match. For American Express: Card member's name, street address and postal code do not match."
    ):
      return ["The address provided does not match the billing address of the cardholder.", "warning"];
    case searchForString(text, "Card Code is required."):
      return cvvText;
    case searchForString(text, "Bill To Address is required."):
      return ["Your billing address is missing. Please add your billing address and try again.", "warning"];
    case searchForString(
      text,
      "CVV2 check cannot be performed as no data was supplied in the authorization request."
    ):
      return cvvText;
    case searchForString(text, "The referenced transaction does not meet the criteria for issuing a credit."):
      return [
        "Your credit card bank needs additional information. Please contact your bank to process this transaction.",
        "error"
      ];
    default:
      return [text, type];
  }
};

// Maps a Stripe error object's `decline_code`/`code` to accurate, cause-specific copy.
// Unlike formatPaymentErrorMessage (which pattern-matches legacy Authorize.net message text),
// this reads Stripe's structured fields directly so the message matches the real decline reason
// instead of relying on substring matches against Stripe's raw wording.
// Decline codes: https://docs.stripe.com/declines/codes
const STRIPE_DECLINE_MESSAGES = {
  insufficient_funds: ["Your card was declined. Please use a valid card.", "error"],
  card_declined: [
    "Your card was declined by your bank. Please try a different card or contact your card issuer for details.",
    "error"
  ],
  expired_card: ["Your card has expired. Please use a different card.", "error"],
  incorrect_cvc: [
    "The security code (CVC) you entered is incorrect. Please check it and try again.",
    "warning"
  ],
  invalid_cvc: ["The security code (CVC) you entered is invalid. Please check it and try again.", "warning"],
  incorrect_number: ["The card number you entered is incorrect. Please check it and try again.", "warning"],
  invalid_expiry_month: [
    "The expiration month on this card is invalid. Please check it and try again.",
    "warning"
  ],
  invalid_expiry_year: [
    "The expiration year on this card is invalid. Please check it and try again.",
    "warning"
  ],
  incorrect_zip: [
    "The zip/postal code you entered doesn't match your card. Please check it and try again.",
    "warning"
  ],
  processing_error: [
    "We hit a processing error trying to verify this card. Please try again in a moment.",
    "error"
  ],
  fraudulent: [
    "Your card issuer flagged this card. Please contact your card issuer or use a different card.",
    "error"
  ],
  lost_card: ["This card has been reported lost. Please use a different card.", "error"],
  stolen_card: ["This card has been reported stolen. Please use a different card.", "error"],
  card_velocity_exceeded: [
    "Your card has hit its purchase limit for this period. Please try a different card or contact your card issuer.",
    "warning"
  ],
  generic_decline: [
    "Your card issuer declined this. Please try a different card or contact your card issuer for details.",
    "error"
  ]
};

export const formatStripeDeclineMessage = error => {
  const declineCode = error?.decline_code || error?.code;
  return STRIPE_DECLINE_MESSAGES[declineCode] || null;
};
